import React, { Fragment, useEffect, useState } from "react";
import {
  Dialog,
  DialogBackdrop,
  DialogTitle,
  Transition,
  TransitionChild,
} from "@headlessui/react";
import Select from "react-select";
import { ISelectElement } from "../../../model/interface/keyValuePair";
import {
  addNewStandard,
  getStandardGroups,
  createStandardGroup,
} from "../../../api/standardGroup.service";
import { useForm, SubmitHandler } from "react-hook-form";
import Loader from "../loader/loader";
import {
  IStandardGroup,
  IStandardGroupDTO,
  IStandardGroupSearchRequest,
  IStandardGroupStandard,
} from "../../../model/interface/standardGroup";
import { connect } from "react-redux";

interface AddStandardGroupProps {
  subjectId: number;
  domainId: number;
  standardId: number;
  existingStandardGroups: Array<IStandardGroupDTO>;
  title: string;
  buttonText: string;
  profile: any;
  confirmAction: (confirm: boolean) => void;
}
const AddStandardGroup = (props: AddStandardGroupProps) => {
  type Inputs = {
    newStandardGroup: string;
    existingStandardGroup: string;
  };

  const { register, handleSubmit } = useForm<Inputs>();
  const [isGroupDisabled, setIsGroupDisabled] = useState<boolean>(true);
  const [isNameDisabled, setIsNameDisabled] = useState<boolean>(true);
  const [loading, setLoading] = useState<boolean>(false);

  const defaultProductValue: ISelectElement = {
    label: "Select Standard Group",
    value: "0",
  };
  const [selectedGroup, setSelectedGroup] =
    useState<ISelectElement>(defaultProductValue);
  const [allGroups, setAllGroups] = useState<any[]>([]);
  const [isExistingSelected, setIsExistingSelected] = useState(false);

  useEffect(() => {
    fetchGroupList();
  }, []);

  const onSubmit: SubmitHandler<Inputs> = (data) => {
    setLoading(true);
    if (isExistingSelected) {
      const standardGroupStandard: IStandardGroupStandard = {
        standardGroupId: parseInt(selectedGroup.value),
        standardId: props.standardId,
        isActive: true,
        createdByUserId: props.profile?.userId,
      };
      addNewStandard(standardGroupStandard)
        .then(() => {
          setLoading(false);
          postSuccess();
        })
        .catch(() => {
          setLoading(false);
        });
    } else {
      const standardGroup: IStandardGroup = {
        standardGroupId: 0,
        name: data.newStandardGroup,
        subjectId: props.subjectId,
        domainId: props.domainId,
        isActive: true,
        createdByUserId: props.profile?.userId,
      };

      const standardGroupStandard: IStandardGroupStandard = {
        standardGroupId: 0,
        standardId: props.standardId,
        isActive: true,
        createdByUserId: props.profile?.userId,
      };

      createStandardGroup(standardGroup, standardGroupStandard)
        .then(() => {
          setLoading(false);
          postSuccess();
        })
        .catch(() => {
          setLoading(false);
        });
    }
  };

  const postSuccess = async () => {
    const module = await import("react-toastify");
    module.toast.success("Standard added successfully");

    setTimeout(() => {
      props.confirmAction(false);
    }, 1000);
  };

  const handleGroupChange = (selectedItem: any) => {
    setIsExistingSelected(true);
    setSelectedGroup(selectedItem);
  };

  const fetchGroupList = () => {
    setLoading(true);
    const standard: IStandardGroupSearchRequest = {
      standardGroupId: 0,
      name: "",
      standardName: "",
      stateId: 0,
      gradeId: 0,
      subjectId: props.subjectId,
      domainId: 0,
      isActive: true,
    };

    getStandardGroups(standard, 1, 9999)
      .then((r) => {
        const newGroupData: Array<any> = [];
        r?.data?.data?.map((group) => {
          if (
            !props?.existingStandardGroups
              ?.map((sg) => sg.standardGroupId)
              .includes(group.standardGroupId)
          )
            newGroupData.push({
              value: group.standardGroupId || 0,
              label: group.name,
            });
        });

        setAllGroups(newGroupData);
        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
      });
  };

  const handleRadioButtonChange = (type: string) => {
    if (type === "existingStandardGroup") {
      setIsGroupDisabled(false);
      setIsNameDisabled(true);
    } else {
      setIsNameDisabled(false);
      setIsGroupDisabled(true);
    }
  };

  return (
    <div>
      {loading && <Loader />}

      <Transition.Root show={true} as={Fragment}>
        <Dialog
          as="div"
          className="fixed z-10 inset-0"
          onClose={() => {
            props.confirmAction(false);
          }}
        >
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <TransitionChild
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <DialogBackdrop className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
            </TransitionChild>

            {/* This element is to trick the browser into centering the modal contents. */}
            <span
              className="hidden sm:inline-block sm:align-middle sm:h-screen"
              aria-hidden="true"
            >
              &#8203;
            </span>
            <TransitionChild
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <div className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left  shadow-xl transform transition-all sm:my-8 sm:align-middle sm:p-6 w-1/4 ">
                <form onSubmit={handleSubmit(onSubmit)}>
                  <div className="hidden sm:block absolute top-0 right-0 pt-4 pr-4"></div>
                  <div className="sm:flex sm:items-start">
                    <div className="w-full mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                      <DialogTitle as="h3" className="text-sm text-gray-700">
                        {props.title}
                      </DialogTitle>
                      <p></p>
                      <div className="mt-2">
                        <div className="grid grid-cols-1 divide-y divide-gray-300 border border-gray-300 rounded-lg ">
                          <div className=" bg-gray-100 h-22 px-4 flex">
                            <div className="flex items-center w-full">
                              <input
                                type="radio"
                                onClick={() =>
                                  handleRadioButtonChange("newStandardGroup")
                                }
                                className="focus:ring-primary-color h-4 w-4 text-primary-color border-gray-300"
                                name="rbtStandardGroup"
                              />
                              <div className="w-full py-4">
                                <label className="ml-3 mb-1 block text-sm font-medium text-gray-700">
                                  Group Name
                                  <input
                                    id="newStandardGroup"
                                    disabled={isNameDisabled}
                                    type="text"
                                    defaultValue=""
                                    className={`${
                                      isNameDisabled ? "bg-gray-100" : ""
                                    } mt-1 block border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-color focus:border-primary-color sm:text-sm w-full`}
                                    {...register("newStandardGroup")}
                                  />
                                </label>
                              </div>
                            </div>
                          </div>
                          <div className=" h-22 px-4 flex w-full">
                            <div className="flex items-center w-full">
                              <input
                                onClick={() =>
                                  handleRadioButtonChange(
                                    "existingStandardGroup"
                                  )
                                }
                                type="radio"
                                className="focus:ring-primary-color h-4 w-4 text-primary-color border-gray-300"
                                name="rbtStandardGroup"
                              />
                              <div className="w-full py-4">
                                <label className="ml-3 mb-1 block text-sm font-medium text-gray-700">
                                  Add to existing standard group
                                  <div className="text-sm mt-1">
                                    <Select
                                      isDisabled={isGroupDisabled}
                                      options={allGroups}
                                      value={selectedGroup}
                                      isSearchable={true}
                                      onChange={handleGroupChange}
                                      theme={(theme) => ({
                                        ...theme,
                                        colors: {
                                          ...theme.colors,
                                          primary25: "#B3B3B3",
                                          primary: "#041E42",
                                        },
                                      })}
                                      maxMenuHeight={250}
                                    ></Select>
                                  </div>
                                </label>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                    <button
                      type="submit"
                      className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-primary-color text-base font-medium text-white hover:bg-primary-color focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-color sm:ml-3 sm:w-auto sm:text-sm"
                    >
                      {props.buttonText}
                    </button>
                    <button
                      type="button"
                      className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-color sm:mt-0 sm:w-auto sm:text-sm"
                      onClick={() => {
                        props.confirmAction(false);
                      }}
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              </div>
            </TransitionChild>
          </div>
        </Dialog>
      </Transition.Root>
    </div>
  );
};

const mapStateToProps = (state: any) => {
  return {
    profile: state.profile,
  };
};
export default connect(mapStateToProps, {})(AddStandardGroup);
